import { useEffect, useState, useRef } from "react";
import { api, fmtDate } from "../lib/api";
import { saveEntry, listEntries, deleteEntry as idbDelete } from "../lib/idb";
import { syncPending, lastSync as fetchLastSync } from "../lib/sync";
import CommentThread from "../components/CommentThread";
import {
  Camera, MapPin, Wifi, WifiOff, Loader2, Plus, X, Trash2, RefreshCw,
  Image as ImageIcon, FileText, Ruler, CheckCircle2, AlertCircle, HardHat, MessageSquare, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

export default function FieldCapture() {
  const [missions, setMissions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [showModal, setShowModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(null);

  const refresh = async () => {
    setEntries(await listEntries());
    setLastSyncAt(await fetchLastSync());
  };

  useEffect(() => {
    refresh();
    const onOnline = () => { setOnline(true); doSync(); };
    const onOffline = () => setOnline(false);
    const onSynced = () => refresh();
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("btp:field-synced", onSynced);

    // Try to load missions/projects from network; cache via IDB? keep simple — they live on server
    api.get("/missions").then((r) => setMissions(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("btp:field-synced", onSynced);
    };
    // eslint-disable-next-line
  }, []);

  const doSync = async () => {
    if (!navigator.onLine) { toast.error("Hors-ligne"); return; }
    setSyncing(true);
    const r = await syncPending();
    setSyncing(false);
    if (r.ok) {
      toast.success(`Sync : ${r.pushed} envoyée(s), ${r.failed} échec(s)`);
      refresh();
    } else {
      toast.error("Sync impossible");
    }
  };

  const pendingCount = entries.filter((e) => !e.synced).length;

  return (
    <div className="space-y-5 max-w-4xl mx-auto pb-24" data-testid="field-page">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-btp-terra font-semibold mb-2">Mode terrain</div>
            <h1 className="font-heading text-2xl sm:text-4xl font-bold tracking-tight flex items-center gap-2">
              <HardHat /> Saisie terrain
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Saisie offline en mission. Les données sont stockées localement et synchronisées dès la reconnexion.
            </p>
          </div>
        </div>
      </div>

      {/* Status bar */}
      <div className="grid grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
        <div className={`bg-white p-3 sm:p-4 ${online ? "" : "border-l-2 border-btp-terra"}`} data-testid="status-network">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Réseau</div>
          <div className={`mt-1 font-heading text-base sm:text-lg font-bold flex items-center gap-1.5 ${online ? "text-emerald-600" : "text-btp-terra"}`}>
            {online ? <><Wifi size={16} /> En ligne</> : <><WifiOff size={16} /> Hors-ligne</>}
          </div>
        </div>
        <div className="bg-white p-3 sm:p-4">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">En attente</div>
          <div className="mt-1 font-heading text-base sm:text-lg font-bold tabular-nums" data-testid="pending-count">
            {pendingCount} <span className="text-xs font-normal text-slate-500">saisie{pendingCount !== 1 ? "s" : ""}</span>
          </div>
        </div>
        <div className="bg-white p-3 sm:p-4">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Dernière sync</div>
          <div className="mt-1 text-sm font-mono">{lastSyncAt ? fmtDate(lastSyncAt) : "—"}</div>
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={() => setShowModal(true)} className="flex-1 h-14 bg-btp-terra hover:bg-btp-terra-hover text-white rounded-md font-semibold flex items-center justify-center gap-2 text-base shadow-md" data-testid="capture-btn">
          <Plus size={20} /> Nouvelle saisie
        </button>
        <button onClick={doSync} disabled={!online || syncing || pendingCount === 0}
          className="h-14 px-5 border-2 border-btp-navy text-btp-navy disabled:opacity-50 rounded-md font-semibold flex items-center gap-2"
          data-testid="sync-btn">
          {syncing ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
          Sync
        </button>
      </div>

      {/* Entries */}
      <div className="space-y-3">
        {entries.length === 0 && (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center">
            <Camera className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">Aucune saisie. Démarrez la capture sur le terrain.</p>
          </div>
        )}
        {entries.map((e) => (
          <EntryCard key={e.local_id} entry={e} missions={missions} projects={projects}
            onDelete={async () => { await idbDelete(e.local_id); refresh(); }} />
        ))}
      </div>

      {showModal && (
        <CaptureModal
          missions={missions}
          projects={projects}
          onClose={() => setShowModal(false)}
          onSaved={async () => { setShowModal(false); await refresh(); if (online) doSync(); }}
        />
      )}
    </div>
  );
}

function EntryCard({ entry, missions, projects, users, onDelete }) {
  const mission = missions.find((m) => m.id === entry.mission_id);
  const project = projects.find((p) => p.id === entry.project_id);
  const Icon = entry.type === "photo" ? ImageIcon : entry.type === "mesure" ? Ruler : FileText;
  const [showComments, setShowComments] = useState(false);
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={`entry-${entry.local_id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${entry.type === "photo" ? "bg-blue-50 text-blue-600" : entry.type === "mesure" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-medium text-slate-900 leading-snug">{entry.title || "Saisie"}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {project?.code && <span className="font-mono mr-1">{project.code}</span>}
                {mission?.name || "Sans mission"}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {entry.synced ? (
                <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 flex items-center gap-1" data-testid="badge-synced">
                  <CheckCircle2 size={10} /> Sync
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 flex items-center gap-1" data-testid="badge-pending">
                  <AlertCircle size={10} /> En attente
                </span>
              )}
            </div>
          </div>
          {entry.description && <p className="text-sm text-slate-700 mt-2 leading-relaxed line-clamp-3">{entry.description}</p>}
          {entry.gps && (
            <div className="mt-2 inline-flex items-center gap-1 text-xs font-mono text-slate-600 bg-slate-50 px-2 py-0.5 rounded">
              <MapPin size={11} /> {entry.gps.lat.toFixed(5)}, {entry.gps.lon.toFixed(5)}
              {entry.gps.accuracy && <span className="text-slate-400 ml-1">±{Math.round(entry.gps.accuracy)}m</span>}
            </div>
          )}
          {entry.photo_b64 && (
            <img src={entry.photo_b64} alt="" className="mt-3 rounded-md w-full max-w-xs border border-slate-200" />
          )}
          <div className="mt-3 flex items-center justify-between">
            <div className="text-[10px] font-mono text-slate-400">{new Date(entry.captured_at).toLocaleString("fr-FR")}</div>
            <div className="flex items-center gap-3">
              {entry.synced && entry.server_id && (
                <button onClick={() => setShowComments((s) => !s)} className="text-xs text-btp-navy hover:underline flex items-center gap-1" data-testid={`toggle-comments-${entry.local_id}`}>
                  <MessageSquare size={12} /> {showComments ? "Masquer" : "Commentaires"} <ChevronDown size={11} className={`transition-transform ${showComments ? "rotate-180" : ""}`} />
                </button>
              )}
              <button onClick={onDelete} className="text-slate-400 hover:text-red-600 text-xs flex items-center gap-1" data-testid={`delete-entry-${entry.local_id}`}>
                <Trash2 size={12} /> Supprimer
              </button>
            </div>
          </div>
          {showComments && entry.synced && entry.server_id && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <CommentThread entityType="field_entry" entityId={entry.server_id} users={users} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CaptureModal({ missions, projects, onClose, onSaved }) {
  const [form, setForm] = useState({
    type: "observation", mission_id: "", project_id: "",
    title: "", description: "",
    measurements: { value: "", unit: "m" },
  });
  const [photo, setPhoto] = useState(null); // data URL
  const [gps, setGps] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onMissionChange = (e) => {
    const mid = e.target.value;
    const m = missions.find((x) => x.id === mid);
    setForm({ ...form, mission_id: mid, project_id: m?.project_id || form.project_id });
  };

  const captureGPS = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation non disponible"); return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGpsLoading(false);
        toast.success("Position acquise");
      },
      (err) => {
        setGpsLoading(false);
        toast.error("Erreur GPS : " + err.message);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const onPhoto = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      // Compress via canvas
      const img = new Image();
      img.onload = () => {
        const max = 1280;
        const ratio = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        setPhoto(canvas.toDataURL("image/jpeg", 0.7));
        if (form.type !== "photo") setForm({ ...form, type: "photo" });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saveEntry({
        ...form,
        gps,
        photo_b64: photo,
        measurements: form.type === "mesure" ? form.measurements : null,
      });
      toast.success(navigator.onLine ? "Saisie enregistrée — synchronisation…" : "Saisie sauvegardée localement");
      onSaved();
    } catch (err) {
      toast.error("Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-md max-h-[95vh] flex flex-col" data-testid="capture-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="font-heading text-lg font-bold">Nouvelle saisie terrain</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-capture"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Type selector – big touch buttons */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "observation", l: "Observation", icon: FileText },
              { v: "photo", l: "Photo", icon: ImageIcon },
              { v: "mesure", l: "Mesure", icon: Ruler },
            ].map((opt) => (
              <button key={opt.v} type="button" onClick={() => setForm({ ...form, type: opt.v })}
                className={`p-3 rounded-md border-2 text-sm font-semibold flex flex-col items-center gap-1.5 transition-colors ${
                  form.type === opt.v ? "border-btp-navy bg-slate-50 text-btp-navy" : "border-slate-200 text-slate-500"
                }`}
                data-testid={`type-${opt.v}`}>
                <opt.icon size={18} />
                {opt.l}
              </button>
            ))}
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Mission</label>
            <select value={form.mission_id} onChange={onMissionChange} className="mt-1 w-full h-12 px-3 border border-slate-300 rounded text-base bg-white" data-testid="capture-mission">
              <option value="">— Sans mission —</option>
              {missions.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Titre</label>
            <input required value={form.title} onChange={setField("title")} placeholder="Ex: Anomalie talus PK 12+500"
              className="mt-1 w-full h-12 px-3 border border-slate-300 rounded text-base" data-testid="capture-title" />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Description</label>
            <textarea rows={4} value={form.description} onChange={setField("description")} placeholder="Notes, observations détaillées…"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded text-sm leading-relaxed" data-testid="capture-desc" />
          </div>

          {form.type === "mesure" && (
            <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 border border-slate-200 rounded">
              <input type="number" step="0.01" placeholder="Valeur" value={form.measurements.value}
                onChange={(e) => setForm({ ...form, measurements: { ...form.measurements, value: e.target.value } })}
                className="h-11 px-3 border border-slate-300 rounded text-base" data-testid="capture-measure-value" />
              <select value={form.measurements.unit}
                onChange={(e) => setForm({ ...form, measurements: { ...form.measurements, unit: e.target.value } })}
                className="h-11 px-3 border border-slate-300 rounded text-base">
                <option value="m">mètres</option>
                <option value="cm">cm</option>
                <option value="mm">mm</option>
                <option value="m2">m²</option>
                <option value="m3">m³</option>
                <option value="kg">kg</option>
                <option value="t">tonnes</option>
                <option value="°">degrés</option>
                <option value="%">%</option>
              </select>
            </div>
          )}

          {/* GPS */}
          <div className="flex items-center gap-2">
            <button type="button" onClick={captureGPS} disabled={gpsLoading}
              className="flex-1 h-12 border border-slate-300 rounded text-sm font-semibold flex items-center justify-center gap-2 hover:bg-slate-50 disabled:opacity-50"
              data-testid="capture-gps">
              {gpsLoading ? <Loader2 size={16} className="animate-spin" /> : <MapPin size={16} />}
              {gps ? `${gps.lat.toFixed(5)}, ${gps.lon.toFixed(5)}` : "Capturer position GPS"}
            </button>
            {gps && (
              <button type="button" onClick={() => setGps(null)} className="text-slate-400 hover:text-red-600 px-2"><X size={16} /></button>
            )}
          </div>

          {/* Photo */}
          <div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="capture-photo-input" />
            <button type="button" onClick={() => fileRef.current?.click()}
              className="w-full h-12 border border-slate-300 rounded text-sm font-semibold flex items-center justify-center gap-2 hover:bg-slate-50"
              data-testid="capture-photo-btn">
              <Camera size={16} /> {photo ? "Remplacer la photo" : "Prendre une photo"}
            </button>
            {photo && (
              <div className="mt-2 relative">
                <img src={photo} alt="capture" className="w-full max-h-64 object-cover rounded border border-slate-200" />
                <button type="button" onClick={() => setPhoto(null)} className="absolute top-2 right-2 bg-white border border-slate-300 rounded-full p-1.5 shadow-sm">
                  <X size={14} />
                </button>
              </div>
            )}
          </div>
        </form>
        <div className="px-5 py-4 border-t border-slate-200 flex gap-3 bg-white">
          <button type="button" onClick={onClose} className="flex-1 h-12 border border-slate-300 rounded text-sm font-medium">Annuler</button>
          <button onClick={submit} disabled={saving || !form.title}
            className="flex-1 h-12 bg-btp-terra hover:bg-btp-terra-hover disabled:opacity-50 text-white rounded text-sm font-bold flex items-center justify-center gap-2"
            data-testid="submit-capture">
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
